import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot,
  Github,
  Sparkles,
  Users,
  TrendingUp,
  User,
  Brain,
  MessageCircle,
  X,
  Rocket,
  Wand2,
  ShoppingCart,
  Video,
  Download,
  Clock,
  Star,
  ArrowRight,
  DollarSign,
  Activity,
  Eye
} from 'lucide-react';
import { AgentCard } from '../components/AgentCard';
import { ScrapeButton } from '../components/ScrapeButton';
import { AIAssistant } from '../components/AIAssistant';
import { AgentBuilder } from '../components/AgentBuilder';
import { DeploymentWorkflow } from '../components/DeploymentWorkflow';
import { MonitoringDashboard } from '../components/MonitoringDashboard';
import { MarketplaceModal } from '../components/MarketplaceModal';
import { AgentShareModal } from '../components/AgentShareModal';
import { TavusVideoIntegration } from '../components/TavusVideoIntegration';
import { TavusVideoHelp } from '../components/TavusVideoHelp';
import { SmartRecommendations } from '../components/SmartRecommendations';
import { PersonalizedDashboard } from '../components/PersonalizedDashboard';
import { useAuth } from '../lib/AuthContext';
import { useAgents } from '../hooks/useAgents';
import { Agent } from '../lib/supabase';

export function DashboardPage() {
  const { user } = useAuth();
  const { agents, loading: agentsLoading, fetchAgents } = useAgents();

  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantInitialMessage, setAssistantInitialMessage] = useState('');
  const [builderOpen, setBuilderOpen] = useState(false);
  const [deploymentOpen, setDeploymentOpen] = useState(false);
  const [monitoringOpen, setMonitoringOpen] = useState(false);
  const [marketplaceOpen, setMarketplaceOpen] = useState(false);
  const [tavusVideoOpen, setTavusVideoOpen] = useState(false);
  const [tavusHelpOpen, setTavusHelpOpen] = useState(false);
  const [selectedAgentForDeployment, setSelectedAgentForDeployment] = useState<any>(null);
  const [selectedAgentForShare, setSelectedAgentForShare] = useState<Agent | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // State for personalized and trending agents
  const [personalizedAgents, setPersonalizedAgents] = useState<Agent[]>([]);
  const [trendingAgents, setTrendingAgents] = useState<Agent[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);

  useEffect(() => {
    // Simulate fetching personalized and trending agents
    // In a real scenario, these would come from dedicated backend services
    if (!agentsLoading && agents.length > 0) {
      setPersonalizedAgents(agents.slice(0, 3)); // Top 3 for personalized
      setTrendingAgents(agents.slice(3, 6)); // Next 3 for trending
      setRecentActivity([
        { type: 'deployed', agentName: agents.name, timestamp: '2 hours ago' },
        { type: 'analyzed', agentName: agents.name, timestamp: '1 day ago' },
        { type: 'downloaded', agentName: agents.name, timestamp: '3 days ago' },
      ]);
    }
  }, [agents, agentsLoading]);

  const handleScrapeComplete = useCallback(() => {
    fetchAgents(); // Refresh agents after scraping
  }, [fetchAgents]);

  const handleQuickDeploy = () => {
    const sampleAgent = agents || {
      id: 'sample', name: 'Sample Agent', description: 'A sample AI agent', source: 'custom',
      source_url: '#', author: 'Agent Store', category: 'Sample', tags: [], language: 'JS',
      framework: 'React', stars: 0, downloads: 0, is_verified: true, metadata: {},
      created_at: new Date().toISOString(), updated_at: new Date().toISOString()
    };
    setSelectedAgentForDeployment(sampleAgent);
    setDeploymentOpen(true);
  };

  const handleShareAgent = (agent: Agent) => {
    setSelectedAgentForShare(agent);
    setShareModalOpen(true);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'deployed': return <Rocket className="w-4 h-4 text-blue-500" />;
      case 'analyzed': return <Brain className="w-4 h-4 text-purple-500" />;
      case 'downloaded': return <Download className="w-4 h-4 text-green-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 p-4 md:p-6">
      {/* Welcome & Overview */}
      <div className="bg-white rounded-2xl shadow-soft p-6 mb-6 border border-dark-200">
        <h1 className="text-2xl md:text-3xl font-bold text-dark-900 mb-2">
          Welcome back, {user?.email?.split('@') || 'Agent Creator'}!
        </h1>
        <p className="text-dark-600 mb-4">Your personalized hub for managing and discovering AI agents.</p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl">
            <Bot className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-xl font-bold text-dark-900">{formatNumber(agents.length)}</p>
              <p className="text-sm text-blue-700">Total Agents</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl">
            <Users className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-xl font-bold text-dark-900">15K+</p>
              <p className="text-sm text-purple-700">Active Users</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl">
            <TrendingUp className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-xl font-bold text-dark-900">50K+</p>
              <p className="text-sm text-green-700">Deployments</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-xl">
            <DollarSign className="w-8 h-8 text-yellow-600" />
            <div>
              <p className="text-xl font-bold text-dark-900">$1.2M</p>
              <p className="text-sm text-yellow-700">Total Revenue</p>
            </div>
          </div>
        </div>
      </div>

      {/* Your Next Steps (Quick Actions) */}
      <div className="bg-white rounded-2xl shadow-soft p-6 mb-6 border border-dark-200">
        <h2 className="text-xl md:text-2xl font-bold text-dark-900 mb-4">Your Next Steps</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => setBuilderOpen(true)}
            className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-xl shadow-medium hover:shadow-lg hover:scale-105 transition-all duration-200 text-center"
          >
            <Wand2 className="w-8 h-8 mb-2" />
            <span className="font-semibold text-lg">Build a New Agent</span>
            <span className="text-sm opacity-90">Create your custom AI</span>
          </button>
          <button
            onClick={handleQuickDeploy}
            className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-blue-500 to-green-500 text-white rounded-xl shadow-medium hover:shadow-lg hover:scale-105 transition-all duration-200 text-center"
          >
            <Rocket className="w-8 h-8 mb-2" />
            <span className="font-semibold text-lg">Deploy an Agent</span>
            <span className="text-sm opacity-90">Get your AI live</span>
          </button>
          <button
            onClick={() => setMarketplaceOpen(true)}
            className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-yellow-500 to-orange-500 text-white rounded-xl shadow-medium hover:shadow-lg hover:scale-105 transition-all duration-200 text-center"
          >
            <ShoppingCart className="w-8 h-8 mb-2" />
            <span className="font-semibold text-lg">Sell Your Agent</span>
            <span className="text-sm opacity-90">Monetize your creations</span>
          </button>
          <Link
            to="/a2a-protocol"
            className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-red-500 to-purple-500 text-white rounded-xl shadow-medium hover:shadow-lg hover:scale-105 transition-all duration-200 text-center"
          >
            <MessageCircle className="w-8 h-8 mb-2" />
            <span className="font-semibold text-lg">Explore A2A Protocol</span>
            <span className="text-sm opacity-90">Connect agents</span>
          </Link>
        </div>
      </div>

      {/* Personalized Agent Feed ("For You") */}
      <div className="bg-white rounded-2xl shadow-soft p-6 mb-6 border border-dark-200">
        <h2 className="text-xl md:text-2xl font-bold text-dark-900 mb-4">Personalized for You</h2>
        {agentsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="card-modern p-4 animate-pulse">
                <div className="h-4 bg-dark-200 rounded mb-4" />
                <div className="h-3 bg-dark-200 rounded mb-2" />
                <div className="h-3 bg-dark-200 rounded mb-4 w-2/3" />
                <div className="h-10 bg-dark-200 rounded" />
              </div>
            ))}
          </div>
        ) : personalizedAgents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {personalizedAgents.map((agent) => (
              <AgentCard key={agent.id} agent={agent} onShareAgent={() => handleShareAgent(agent)} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-dark-600">
            <Brain className="w-12 h-12 mx-auto mb-4 text-dark-300" />
            <p>No personalized recommendations yet. Interact with more agents to get tailored suggestions!</p>
          </div>
        )}
      </div>

      {/* What's Popular Now (Trending Agents) */}
      <div className="bg-white rounded-2xl shadow-soft p-6 mb-6 border border-dark-200">
        <h2 className="text-xl md:text-2xl font-bold text-dark-900 mb-4">What's Popular Now</h2>
        {agentsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="card-modern p-4 animate-pulse">
                <div className="h-4 bg-dark-200 rounded mb-4" />
                <div className="h-3 bg-dark-200 rounded mb-2" />
                <div className="h-3 bg-dark-200 rounded mb-4 w-2/3" />
                <div className="h-10 bg-dark-200 rounded" />
              </div>
            ))}
          </div>
        ) : trendingAgents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {trendingAgents.map((agent) => (
              <AgentCard key={agent.id} agent={agent} onShareAgent={() => handleShareAgent(agent)} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-dark-600">
            <TrendingUp className="w-12 h-12 mx-auto mb-4 text-dark-300" />
            <p>No trending agents to display right now. Check back later!</p>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-2xl shadow-soft p-6 mb-6 border border-dark-200">
        <h2 className="text-xl md:text-2xl font-bold text-dark-900 mb-4">Your Recent Activity</h2>
        {recentActivity.length > 0 ? (
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="p-2 bg-gray-100 rounded-full">
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-dark-900">
                    You {activity.type} <span className="text-blue-600">{activity.agentName}</span>
                  </p>
                  <p className="text-sm text-gray-600">{activity.timestamp}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-dark-600">
            <Clock className="w-12 h-12 mx-auto mb-4 text-dark-300" />
            <p>No recent activity to display. Start interacting with agents!</p>
          </div>
        )}
      </div>

      {/* Modals */}
      <AIAssistant
        isOpen={assistantOpen}
        onToggle={() => setAssistantOpen(!assistantOpen)}
        initialMessage={assistantInitialMessage}
        setInitialMessage={setAssistantInitialMessage}
      />
      <AgentBuilder
        isOpen={builderOpen}
        onClose={() => setBuilderOpen(false)}
        onOpenAssistant={() => setAssistantOpen(true)}
        onSetAssistantInitialMessage={setAssistantInitialMessage}
      />
      {selectedAgentForDeployment && (
        <DeploymentWorkflow
          agent={selectedAgentForDeployment}
          isOpen={deploymentOpen}
          onClose={() => {
            setDeploymentOpen(false);
            setSelectedAgentForDeployment(null);
          }}
        />
      )}
      <MonitoringDashboard
        isOpen={monitoringOpen}
        onClose={() => setMonitoringOpen(false)}
      />
      <MarketplaceModal
        isOpen={marketplaceOpen}
        onClose={() => setMarketplaceOpen(false)}
      />
      <TavusVideoIntegration
        isOpen={tavusVideoOpen}
        onClose={() => setTavusVideoOpen(false)}
        apiKey="c415e23a929f4c4d811066c32ee4ed0a"
      />
      <TavusVideoHelp
        isOpen={tavusHelpOpen}
        onClose={() => setTavusHelpOpen(false)}
      />
      {selectedAgentForShare && (
        <AgentShareModal
          agent={selectedAgentForShare}
          isOpen={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setSelectedAgentForShare(null);
          }}
        />
      )}
    </div>
  );
}